import { Injectable } from '@zhfe/vue2-di-plugin'
import { Store } from 'vuex'
import { EventBusService } from '@/services/event-bus.service'
import { Subject } from 'rxjs'
import { PERMISSION_VIP_TYPE } from '@/config/dataMap'

export interface BindInfo {
  /** [2]普通账号是否有绑定过游客 */
  has_server_id?: boolean
  /** [1]游客所绑定的账号信息 */
  open_id?: string
  /** [3]普通账号所绑定的最后一次游客信息(需要return_tourist=1) */
  t_open_id?: number
  /** [1]游客所绑定的账号信息 */
  token?: string
}
export interface PermissionVipInfoItem{
  vip_type?: string
  is_continuous?: boolean
  /** vip到期时间戳 */
  vip_ex_date?: number
}
export interface PrivilegePermissionInfo {
  /** 剩下可用服务次数 */
  service_times: number
  /** 服务到期UNIX时间戳 */
  service_expire_time: number
}
export interface PermissionInfo {
  /** 服务名，name为data_recovery、sj_recovery等，见备注json格式 */
   [name: string]: PrivilegePermissionInfo
}
export interface UserInfo {
  open_id: string
  /** 用户来源（0:未知 1:微信 2:QQ 3:手机） */
  register_frm: number
  /** 登录来源（0:未知 1:微信 2:QQ 3:手机 90:老会员） */
  login_frm: number
  /** 所在地 */
  address?: string
  /** 头像url */
  avatar?: string
  bind_info?: BindInfo
  /** 生日 */
  birthday?: string
  /** 服务器当前时间 */
  current_time?: number
  /** 学历 */
  education?: number
  /** 邮箱地址 */
  email?: string
  /** 性别 */
  gender?: number
  /** 是否为包时段用户,"0"为非,"1"为是 */
  is_continuous?: string
  /** 是否游客账号 */
  is_tourist?: boolean
  /** 会员过期时间.如当前用户从未充值会员返回0,如当前用户为会员,返回当前会员过期时间;如用户曾经是会员,则返回最后一次会员过期时间 */
  last_vip_expire_time?: number
  /** 在其他终端发起的订单，绑定了游客账号后，该订单号是当时从 H5 界面上下的单，定期查询该订单用于若用户支付成功，可以立即刷新会员状态 */
  latest_order_id?: string
  /** 会员返现用。最后订单的支付类型，0其它、1 一个月、2 三个月、3六个月、4十二个月、5是0.99套餐、6三年、7钻石会员 */
  latest_order_type?: string
  /** 绑定手机 */
  mobile?: string
  /** 用户名 */
  nickname?: string
  /** 职业 */
  profession?: number
  /** 签名 */
  sign?: string
  /** vip到期时间戳，非vip用户为空 */
  vip_ex_date?: number
  /** "0"为没有子类型，"1"为荣誉会员 */
  vip_sub_type?: string
  /** "0"为非vip用户，"1"为体验用户，"2"为付费用户，"3"为钻石用户，"5"为超级会员 */
  vip_type?: string
}

@Injectable()
export class UserService {
  onLogout = new Subject<void>()
  onShowPay = new Subject<void>()
  /** 用户信息改变事件（登录，支付 等触发场景） */
  onUserInfoChanged = new Subject<void>()

  constructor (private store: Store<any>,
    private eventBus: EventBusService
  ) {
    // 开关防止死循环
    let isLogoutFromSelf = false
    let isShowPay = false

    eventBus.$on('logout', () => {
      isLogoutFromSelf = true
      this.onLogout.next()
      isLogoutFromSelf = false
      this.onUserInfoChanged.next()
    })

    this.onLogout.subscribe(() => {
      if (isLogoutFromSelf) {
        return
      }
      eventBus.$emit('logout')
    })

    eventBus.$on('showPay', () => {
      isShowPay = true
      this.onShowPay.next()
      isShowPay = false
    })
    this.onShowPay.subscribe((tab) => {
      if (isShowPay) {
        return
      }
      eventBus.$emit('showPay', tab)
    })

    this.eventBus.$on('user_refresh', () => {
      this.onUserInfoChanged.next()
    })
  }

  get userInfo (): UserInfo {
    return this.store.state.user.userInfo
  }

  get isLogin (): boolean {
    return this.store.state.user.loginStatus && this.store.state.user.userInfo
  }

  get isUserLogin (): boolean {
    return this.store.state.user.loginStatus === 1
  }

  get token (): string {
    return this.store.state.user?.token || ''
  }

  get openId (): string {
    return this.store.state.user.userInfo?.open_id || ''
  }

  /** 是否为Vip会员 */
  get isVip (): boolean {
    return this.store.getters.isVip
  }

  /** 是否为会员 */
  get isMember (): boolean {
    return this.store.getters.isMember
  }

  /** 普通会员 */
  get normalMember (): boolean {
    return this.store.getters.normalMember
  }

  /** 超级会员 */
  get isSuperMember (): boolean {
    return this.store.getters.isSuperMember
  }

  /** 钻石会员 */
  get isDiamondMember (): boolean {
    return this.store.getters.isDiamondMember
  }

  /** 是否为PDF会员 */
  get isPdfMember (): boolean {
    return this.store.getters.isPdfMember
  }

  /** 是否为PDF终身会员 */
  get isPdfLifeMember (): boolean {
    return this.store.getters.isPdfLifeMember
  }

  /** 权益会员 */
  get getPermissionVIP ():boolean {
    return this.store.getters.getPermissionVIP
  }

  /** 超级权益会员 */
  get getPermissionSVIP ():boolean {
    return this.store.getters.getPermissionSVIP
  }

  /** 是否为视频转换会员 */
  get isVideoMember (): boolean {
    return this.store.getters.isVideoVip
  }

  /** 是否为视频终身转换会员 */
  get isVideoLifeMember (): boolean {
    return this.store.getters.isVideoLifeMember
  }

  /** 是否为续费会员 */
  get isContinuous ():boolean {
    return this.store.getters.isContinuous
  }

  /** 用户头像URL */
  get avatarUrl (): string {
    return this.store.getters.avatar
  }

  /** 登录状态 */
  get loginStatus (): boolean {
    return !!this.store.getters.loginStatus
  }

  /** 用户购买的权限信息 */
  get permissionInfo ():PermissionInfo {
    return this.store.getters.permissionInfo
  }

  /** 服务器当前时间 */
  get currentTime (): number {
    return this.userInfo?.current_time || 0
  }

  /** 特权 */
  get privilege ():string | null{
    return this.store.getters.privilege || null
  }

  /** 刷新 */
  refresh ():Promise<any> {
    return this.store.dispatch('user/refresh', {
      token: this.token,
      open_id: this.openId,
      webRefresh: 1
    }).then(() => {
      this.onUserInfoChanged.next()
    })
  }

  /** 设置用户登录状态 */
  setLoginStatus (status:number) {
    this.store.dispatch('user/setLoginStatus', status)
  }

  /** 根据vip类型获取权限 */
  getPermissionVipInfoItem (vipType: number):PermissionVipInfoItem {
    if (!this.store.state.user.permissionVipInfo || !vipType) {
      return null
    }
    return this.store.state.user.permissionVipInfo.find(item => +item.vip_type === vipType)
  }

  /** PDF会员权益 */
  get pdfPermissionVipInfo ():PermissionVipInfoItem {
    return this.getPermissionVipInfoItem(PERMISSION_VIP_TYPE.member)
  }
}
